import { useMemo } from 'react';

import { AppQueryArg } from '@/types/query.type';
import { logDebug } from '@/util/log.util';

import { useInteractiveCallback } from './useInteractiveCallback';
import { router } from '@inertiajs/react';

type UsQueryParamPayload<T extends string | undefined> = {
    name: AppQueryArg;
    allowedValues?: T[];
    defaultValue?: T;
};

/**
 * A hook that returns the initial value of a query parameter
 * @param name - The name of the query parameter
 * @param allowedValues - The allowed values for the query parameter
 * @param defaultValue - The default value for the query parameter if the query parameter is not found or is not in the allowed values
 * @returns The initial value of the query parameter (if query parameter changes, the hook will still return the initial value) and a function to set the query parameter
 */
export function useQueryParam<T extends string | undefined>({
    name,
    allowedValues,
    defaultValue,
}: UsQueryParamPayload<T>) {
    const queryParam = useMemo(() => {
        try {
            const urlParams = new URLSearchParams(window.location.search);

            const queryParamValue = urlParams.get(name);

            if (
                !queryParamValue ||
                (Array.isArray(allowedValues) &&
                    !allowedValues?.includes(queryParamValue as T))
            ) {
                return defaultValue;
            }

            return decodeURIComponent(queryParamValue) as T;
        } catch {
            return defaultValue;
        }
    }, []);

    const setQueryParam = useInteractiveCallback((value: T | null) => {
        try {
            const nextUrl = new URL(window.location.href);

            if (value === null || value === undefined) {
                if (nextUrl.searchParams.has(name)) {
                    nextUrl.searchParams.delete(name);
                }
            } else {
                nextUrl.searchParams.set(name, value);
            }

            router.push({
                url: nextUrl.pathname + nextUrl.search,
                preserveState: true,
                preserveScroll: true,
            });
        } catch (ex) {
            logDebug('[useQueryParam] Error setting query parameter', ex);
        }
    });

    return [queryParam, setQueryParam] as const;
}
